// buildTimelineModel — pure orchestration of the Chronological Timeline view
// model. Transforms `(trip, ctx, now)` into the `{ overview, phases, errors }`
// shape that `TimelineView` renders.
//
// It composes the existing pure kernels rather than reimplementing any of their
// logic:
//   - `buildJourneyEvents`        — reused event assembly (hotels, dining,
//                                    meetings, car rentals, per-leg flight
//                                    stage events) plus its `errors` channel.
//   - `deriveDepartureSteps`      — granular Departure-phase steps anchored on
//                                    the outbound leg's engine boundaries.
//   - `eventToStep`               — maps Destination-phase events (flight
//                                    arrival, hotel check-in, dining, meetings,
//                                    ground transport) onto Trip_Steps.
//   - `deriveReturnSteps`         — granular Return-phase steps anchored on the
//                                    inbound (homeward) leg.
//   - `assignStepStatuses`        — completed / in-progress / upcoming, with the
//                                    global single-in-progress guarantee.
//   - `groupIntoPhases`           — partition into departure/destination/return.
//   - `sortStepsChronologically`  — chronological ordering within each phase.
//   - `buildOverview`             — the Trip_Overview_Card summary model.
//
// The function is TOTAL: it never throws for any trip shape. A trip with no
// flights yields `{ overview, phases: [], errors }`; malformed legs, missing
// rich data (home address, parking, traffic), and invalid dates are degraded
// gracefully by the underlying kernels and surfaced as non-fatal `errors`
// entries rather than exceptions.
//
// Validates: Requirements 2.1, 4.1, 5.5, 13.5

import { buildJourneyEvents } from './buildJourneyEvents';
import {
  deriveDepartureSteps,
  deriveReturnSteps,
  eventToStep,
} from './deriveJourneySteps';
import { groupIntoPhases } from './groupIntoPhases';
import { sortStepsChronologically } from './sortStepsChronologically';
import { assignStepStatuses } from './assignStepStatuses';
import { buildOverview } from './buildOverview';

// ── Phase presentation metadata ─────────────────────────────
// Static title + badge per phase (Req 2.2–2.5). Descriptions are enriched per
// trip with a date scope below.
const PHASE_META = {
  departure: {
    title: 'Departure',
    scope: 'Getting to your departure gate',
    badge: { color: 'blue', label: 'Outbound' },
  },
  destination: {
    title: 'Destination',
    scope: 'Your time at the destination',
    badge: { color: 'green', label: 'Destination' },
  },
  return: {
    title: 'Return journey',
    scope: 'Your journey back home',
    badge: { color: 'red', label: 'Return' },
  },
};

// ── Helpers ─────────────────────────────────────────────────

/**
 * True when `d` is a usable Date (a real instant).
 * @param {*} d
 * @returns {boolean}
 */
function isValidDate(d) {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

/**
 * Normalize an airport code to a trimmed uppercase string, or null.
 * @param {*} code
 * @returns {string|null}
 */
function normalizeCode(code) {
  return typeof code === 'string' && code.trim() !== ''
    ? code.trim().toUpperCase()
    : null;
}

/**
 * Find the index of the flight leg that returns to the journey origin, mirroring
 * `buildOverview.findReturnLeg`. The origin is the first leg's departure
 * airport; a return leg is the latest later leg whose arrival airport matches
 * that origin. Returns -1 when no homeward leg exists (one-way trip).
 *
 * @param {Array<Object>} flights
 * @returns {number}
 */
function findReturnLegIndex(flights) {
  if (flights.length < 2) return -1;
  const origin = normalizeCode(flights[0]?.departureAirport);
  if (!origin) return -1;
  for (let i = flights.length - 1; i >= 1; i -= 1) {
    if (normalizeCode(flights[i]?.arrivalAirport) === origin) return i;
  }
  return -1;
}

/**
 * Build a date-scoped phase description (Req 2.2), e.g.
 * "Sun, Jun 15 · Getting to your departure gate". Falls back to the scope text
 * alone when no step carries a usable date.
 *
 * @param {Array<{scheduledStart: Date}>} steps
 * @param {string} scope
 * @returns {string}
 */
function phaseDescription(steps, scope) {
  const times = steps
    .map((s) => s && s.scheduledStart)
    .filter(isValidDate)
    .map((d) => d.getTime());
  if (times.length === 0) return scope;
  const earliest = new Date(Math.min(...times));
  const dateLabel = earliest.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return `${dateLabel} · ${scope}`;
}

/**
 * Normalize any error entry to the TimelineModel `{ message }` shape, preserving
 * a `legIndex` when present (harmless extra field).
 *
 * @param {{ message?: string, legIndex?: number }} err
 * @returns {{ message: string }}
 */
function normalizeError(err) {
  if (!err || typeof err !== 'object') {
    return { message: String(err) };
  }
  const message = typeof err.message === 'string' ? err.message : String(err);
  return err.legIndex != null ? { message, legIndex: err.legIndex } : { message };
}

/**
 * Build the Destination-phase steps from the assembled events.
 *
 * Inbound-leg stage events are skipped (the return flight is handled by
 * deriveReturnSteps); only steps that land in the `destination` phase are kept
 * so return-side events (hotel checkout, etc.) don't duplicate.
 *
 * `computeStageTimeline` emits BOTH an in-flight stage (ending at landing) and
 * an arrival-navigation stage (starting at landing) for the same leg, and
 * `eventToStep` maps both onto a single "Flight arrival" step type. These are
 * collapsed to one step per leg, preferring the arrival-navigation variant
 * (which carries a proper scheduledEnd interval) and falling back to the
 * in-flight variant when the arrival-navigation timing is unavailable.
 *
 * @param {Array<Object>} events
 * @param {number} returnLegIndex
 * @param {Object} eventCtx
 * @returns {Array<Object>}
 */
function buildDestinationSteps(events, returnLegIndex, eventCtx) {
  const destinationSteps = [];
  const flightArrivalIdxByLeg = new Map();

  for (const event of events) {
    if (isInboundStageEvent(event, returnLegIndex)) continue;

    const step = eventToStep(event, eventCtx);
    if (!step || step.phase !== 'destination') continue;

    if (step.stepType === 'flight_arrival') {
      const legKey = event.legIndex == null ? '__none__' : event.legIndex;
      const existingIdx = flightArrivalIdxByLeg.get(legKey);
      if (existingIdx != null) {
        // Already have a flight-arrival step for this leg; keep the variant with
        // a defined scheduledEnd (the arrival-navigation interval) over the
        // open-ended in-flight variant.
        const existing = destinationSteps[existingIdx];
        if (existing.scheduledEnd == null && step.scheduledEnd != null) {
          destinationSteps[existingIdx] = step;
        }
        continue;
      }
      flightArrivalIdxByLeg.set(legKey, destinationSteps.length);
    }

    destinationSteps.push(step);
  }

  return destinationSteps;
}

/**
 * True for a `stage` event that belongs to the inbound (homeward) leg, which is
 * handled by deriveReturnSteps and must not be mapped into the destination phase.
 *
 * @param {Object} event
 * @param {number} returnLegIndex
 * @returns {boolean}
 */
function isInboundStageEvent(event, returnLegIndex) {
  return Boolean(
    event &&
      event.type === 'stage' &&
      returnLegIndex !== -1 &&
      event.legIndex === returnLegIndex,
  );
}

/**
 * Build the Return-phase steps from the inbound leg, when a homeward leg exists.
 * deriveReturnSteps reads `trip.userProfile`, so the resolved profile is threaded
 * through. Returns the steps and pushes any non-fatal notes onto `errors`.
 *
 * @param {Object|null} inboundLeg
 * @param {Object} trip
 * @param {Object|null} inboundDepCalc
 * @param {*} userProfile
 * @param {Array<{message: string}>} errors  Mutated with normalized notes.
 * @returns {Array<Object>}
 */
function buildReturnSteps(inboundLeg, trip, inboundDepCalc, userProfile, errors) {
  if (!inboundLeg) return [];

  const hotels = Array.isArray(trip && trip.hotels) ? trip.hotels : [];
  const checkoutHotel = hotels.length > 0 ? hotels[hotels.length - 1] : null;
  const tripForReturn =
    trip && typeof trip === 'object' ? { ...trip, userProfile } : { userProfile };

  const { steps, errors: retErrors } = deriveReturnSteps(
    inboundLeg,
    checkoutHotel,
    inboundDepCalc,
    tripForReturn,
  );
  pushErrors(errors, retErrors);
  return steps;
}

/**
 * Normalize and append a batch of error entries onto the accumulator.
 *
 * @param {Array<{message: string}>} errors
 * @param {Array<Object>|undefined} incoming
 */
function pushErrors(errors, incoming) {
  if (!Array.isArray(incoming)) return;
  for (const e of incoming) errors.push(normalizeError(e));
}

/**
 * Enrich a grouped phase with presentation metadata and chronologically-ordered
 * steps.
 *
 * @param {{ id: string, steps: Array<Object> }} phase
 * @returns {Object}
 */
function decoratePhase(phase) {
  const meta = PHASE_META[phase.id] || {
    title: phase.id,
    scope: '',
    badge: { color: 'grey', label: phase.id },
  };
  const sortedSteps = sortStepsChronologically(phase.steps);
  return {
    id: phase.id,
    title: meta.title,
    description: phaseDescription(sortedSteps, meta.scope),
    badge: meta.badge,
    steps: sortedSteps,
  };
}

/**
 * Normalize the optional `ctx` and `now` arguments into the resolved values the
 * orchestrator needs: the departure-calculation map, the effective traveler
 * profile, the safe context object, and a guaranteed-valid clock.
 *
 * @param {Object} trip
 * @param {Object} [ctx]
 * @param {Date} now
 * @returns {{ safeCtx: Object, departureCalculations: Object, userProfile: *, safeNow: Date }}
 */
function resolveContext(trip, ctx, now) {
  const safeCtx = ctx && typeof ctx === 'object' ? ctx : {};
  const departureCalculations =
    safeCtx.departureCalculations && typeof safeCtx.departureCalculations === 'object'
      ? safeCtx.departureCalculations
      : {};
  const userProfile = safeCtx.userProfile || (trip && trip.userProfile) || undefined;
  const safeNow = isValidDate(now) ? now : new Date();
  return { safeCtx, departureCalculations, userProfile, safeNow };
}

// ── Main ────────────────────────────────────────────────────

/**
 * Build the chronological timeline view model.
 *
 * @param {Object} trip  Full trip object (flights, hotels, carRentals, meetings,
 *                       diningReservations, parking, userProfile).
 * @param {Object} [ctx] Context maps: `{ flightStatuses, departureCalculations,
 *                       journeyDataMap, userProfile }`. All optional.
 * @param {Date}   now   Current clock value (drives status assignment).
 * @returns {{ overview: Object, phases: Array<Object>, errors: Array<{message: string}> }}
 */
export function buildTimelineModel(trip, ctx, now) {
  const errors = [];

  const { safeCtx, departureCalculations, userProfile, safeNow } = resolveContext(
    trip,
    ctx,
    now,
  );

  // 1. Reuse the existing event assembly (and surface its non-fatal errors).
  const { events, errors: eventErrors } = buildJourneyEvents(trip, safeNow, safeCtx);
  pushErrors(errors, eventErrors);

  const flights = Array.isArray(trip && trip.flights) ? trip.flights : [];

  // 2. No flights ⇒ no journey timeline. Still return a fully-built overview so
  //    the card renders, with empty phases (per the design's totality contract).
  if (flights.length === 0) {
    return {
      overview: buildOverview(trip, []),
      phases: [],
      errors,
    };
  }

  // 3. Resolve the outbound (first) and inbound (homeward) legs.
  const outboundLeg = flights[0];
  const outboundDepCalc = departureCalculations[0] || null;

  const returnLegIndex = findReturnLegIndex(flights);
  const inboundLeg = returnLegIndex !== -1 ? flights[returnLegIndex] : null;
  const inboundDepCalc =
    returnLegIndex !== -1 ? departureCalculations[returnLegIndex] || null : null;

  // 4. Granular Departure-phase steps from the outbound leg.
  const { steps: departureSteps, errors: depErrors } = deriveDepartureSteps(
    outboundLeg,
    outboundDepCalc,
    userProfile,
    trip,
  );
  pushErrors(errors, depErrors);

  // 5. Destination-phase steps mapped from the assembled events.
  const eventCtx = { userProfile, departureCalc: outboundDepCalc };
  const destinationSteps = buildDestinationSteps(events, returnLegIndex, eventCtx);

  // 6. Granular Return-phase steps from the inbound leg (when a homeward leg
  //    exists).
  const returnSteps = buildReturnSteps(
    inboundLeg,
    trip,
    inboundDepCalc,
    userProfile,
    errors,
  );

  // 7. Assign statuses across the WHOLE timeline so the single-in-progress
  //    guarantee (Req 4.6) holds globally, not just per phase.
  const allSteps = [...departureSteps, ...destinationSteps, ...returnSteps];
  const statusedSteps = assignStepStatuses(allSteps, safeNow);

  // 8. Partition into phases, then order each phase chronologically (Req 5.5)
  //    and enrich with presentation metadata (Req 2.2–2.5).
  const grouped = groupIntoPhases(statusedSteps);
  const phases = grouped.map(decoratePhase);

  // 9. Overview summary derived from the statused steps.
  const overview = buildOverview(trip, statusedSteps);

  return { overview, phases, errors };
}

export default buildTimelineModel;
